import type {
  AddCorrelation,
  CorrelationError,
  CorrelationsAddCorrelation
} from '@library/types/correlations';
import { Checkbox, Input, NumberInput } from '@library/components/inputs';
import {
  CorrelationsChoiceFilter,
  CorrelationsStatTypeSelect
} from '@bo-components/correlations';
import { Button } from '@library/components/buttons';
import type { ComboboxOption } from '@library/components/selects';
import TrashIcon from '@library/assets/images/icons/trash-icon.svg';
import { useMemo } from 'react';

export interface AddCorrelationsRowProps {
  allStatTypes: Array<ComboboxOption>;
  correlation: AddCorrelation;
  correlationPublishError: CorrelationError | undefined;
  onCorrelationsChange: (correlation: CorrelationsAddCorrelation) => void;
  onDeleteCorrelation: ({ correlationId }: { correlationId: string }) => void;
}

export const AddCorrelationsRow = ({
  allStatTypes,
  correlation,
  correlationPublishError,
  onCorrelationsChange,
  onDeleteCorrelation
}: AddCorrelationsRowProps) => {
  const {
    approvedBy,
    correlationId,
    firstChoice,
    firstPosition,
    firstStatType,
    isSameTeam,
    probability,
    requestedBy,
    secondChoice,
    secondPosition,
    secondStatType
  } = correlation;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialCorrelation = useMemo(() => correlation, []);

  const handleValueChange = ({
    key,
    value
  }: {
    key: string;
    value: boolean | number | string;
  }) => {
    onCorrelationsChange({ initialCorrelation, key, value });
  };

  const error = correlationPublishError
    ? JSON.stringify(correlationPublishError.error)
    : undefined;

  return (
    <>
      <tr className="border-soFresh-140 text-soClean-100 placeholder:text-soClean-140 border-t">
        <td className="py-5 pl-4">
          <Input
            className="bg-soFresh-110 border-soFresh-140 block h-9 w-20 rounded-md border py-4 sm:text-sm sm:leading-6"
            onValueChange={(value) =>
              handleValueChange({ key: 'firstPosition', value })
            }
            value={firstPosition ?? ''}
          />
        </td>
        <td className="py-5 pl-4">
          <CorrelationsStatTypeSelect
            allStatTypes={allStatTypes}
            id="First Stat Type"
            onChange={(value) =>
              handleValueChange({ key: 'firstStatType', value })
            }
            value={firstStatType}
          />
        </td>
        <td className="py-5 pl-4">
          <CorrelationsChoiceFilter
            id="First Choice"
            value={firstChoice}
            onChange={(value) =>
              handleValueChange({ key: 'firstChoice', value })
            }
          />
        </td>
        <td className="py-5 pl-4">
          <Input
            className="bg-soFresh-110 border-soFresh-140 placeholder:text-soClean-140 block h-9 w-20 rounded-md border sm:text-sm sm:leading-6"
            onValueChange={(value) =>
              handleValueChange({ key: 'secondPosition', value })
            }
            value={secondPosition ?? ''}
          />
        </td>
        <td className="py-5 pl-4">
          <CorrelationsStatTypeSelect
            allStatTypes={allStatTypes}
            id="Second Stat Type"
            onChange={(value) =>
              handleValueChange({ key: 'secondStatType', value })
            }
            value={secondStatType}
          />
        </td>
        <td className="py-5 pl-4">
          {' '}
          <CorrelationsChoiceFilter
            id="Second Choice"
            value={secondChoice}
            onChange={(value) =>
              handleValueChange({ key: 'secondChoice', value })
            }
          />
        </td>
        <td className="py-5 pl-4">
          <NumberInput
            className="focus:border-msJackson bg-soFresh-120 border-soFresh-140 h-9 w-14 justify-center rounded-md p-0 text-center text-sm font-medium leading-3 tracking-tight"
            onValueChange={(value) =>
              handleValueChange({ key: 'probability', value })
            }
            placeholder="-"
            value={probability ?? ''}
          />
        </td>
        <td className="py-5 pl-4">
          <div className="w-24 pl-6">
            <Checkbox
              checked={isSameTeam}
              disabled={false}
              id={`${correlationId}-isSameTeam`}
              onCheckboxChange={({ checked }) =>
                handleValueChange({ key: 'isSameTeam', value: checked })
              }
            />
          </div>
        </td>
        <td className="py-5 pl-4">
          <Input
            className="bg-soFresh-110 border-soFresh-140 placeholder:text-soClean-140 block h-9 w-40 rounded-md border py-4 sm:text-sm sm:leading-6"
            onValueChange={(value) =>
              handleValueChange({ key: 'requestedBy', value })
            }
            value={requestedBy ?? ''}
          />
        </td>
        <td className="py-5 pl-4">
          <Input
            className="bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-140 block h-9 w-40 rounded-md border py-4 sm:text-sm sm:leading-6"
            onValueChange={(value) =>
              handleValueChange({ key: 'approvedBy', value })
            }
            value={approvedBy ?? ''}
          />
        </td>
        <td className="h-9 py-5 pl-4">
          <Button
            className="mr-4 w-8"
            onClick={() => {
              onDeleteCorrelation({ correlationId });
            }}
          >
            <img
              alt="Delete Correlation"
              className="size-4 cursor-pointer"
              title="remove card"
              src={TrashIcon}
            />
          </Button>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={4} className="text-roses">
            {error}
          </td>
        </tr>
      )}
    </>
  );
};
